import React, { useState, useEffect, useCallback, useRef } from 'react';
import PageHeader from '../components/PageHeader';
import api from '../services/api';
import io from 'socket.io-client';
import { socketOrigin } from '../services/socketOrigin';
import toast from 'react-hot-toast';
import { AlertTriangle, CheckCircle, Clock, Eye, FileText, Landmark, Lock, RefreshCw, Upload, XCircle } from 'lucide-react';
import {
  buildChecklist, lifecycleLabel, needsIssuedAndExpiry, revisionRows,
  statusPresentation, uploadActionLabel, uploadOptionsFor,
} from '../lib/documentChecklist';
import { nurDatum } from '../lib/zeit';

// ─────────────────────────────────────────────────────────────────────────────
// DOC-LIFECYCLE-001 (Phase G) — Dokumentseite des Partner-Portals.
//
//  • Der Katalog kommt VOLLSTÄNDIG aus dem Backend (GET /partners/onboarding →
//    documents[] je Katalog-Punkt mit lifecycle_status/effective/latest/renewal/…).
//    KEINE eigene Pflichtliste im Frontend mehr.
//  • Voll nutzbar auch NACH der Kontofreigabe — und bei GESPERRTEM Konto
//    (suspended_reason): der Upload-/Erneuerungspfad bleibt offen, genau damit
//    die abgelaufene Urkunde nachgereicht werden kann.
//  • Vorschau eigener Dokumente über die Owner-Route (Blob + Authorization-Header
//    der axios-Instanz, createObjectURL → window.open → revoke). NIE ?token=-Links.
//  • effective (wirksam freigegeben) und latest (neueste Einreichung) werden
//    GETRENNT gezeigt — pending heißt NUR eingereicht, niemals freigegeben.
//  • Aktualisierung bei Rückkehr (visibilitychange/focus) UND per Socket-Event
//    'document_decided' (Raum partner_<uid>, Vertrag Stufe C).
// ─────────────────────────────────────────────────────────────────────────────

// Zusätzliche, OPTIONALE Upload-Wege (Spiegel der optionalen Backend-Katalog-Punkte
// versicherung/sonstiges, required:false — keine Pflicht, nur ein Angebot).
const OPTIONAL_UPLOADS = [
  { type: 'versicherung', label: 'Versicherungsnachweis' },
  { type: 'sonstiges', label: 'Sonstiges' },
];

const stateIcon = {
  approved: <CheckCircle size={15} color="#9C27B0" />,
  renewalPending: <RefreshCw size={15} color="#4b5563" />,
  pendingReview: <Clock size={15} color="#4b5563" />,
  uploading: <Clock size={15} color="#4b5563" />,
  rejected: <XCircle size={15} color="#dc2626" />,
  expired: <AlertTriangle size={15} color="#dc2626" />,
  complianceLocked: <Lock size={15} color="#dc2626" />,
  missing: <FileText size={15} color="#6b7280" />,
};

export default function Documents() {
  const [st, setSt] = useState(null);            // GET /partners/onboarding (Katalog + Lifecycle)
  const [me, setMe] = useState(null);            // GET /partners/me (partner + alle Dokumentzeilen)
  const [uploading, setUploading] = useState({});      // Client-Phase 'uploading' je document_type
  const [dates, setDates] = useState({});              // issue_date/expires_at je Katalog-Punkt (Konzession)
  const [bank, setBank] = useState({ iban: '', bic: '', bank_holder: '' });
  const [optType, setOptType] = useState('versicherung');
  const [loadError, setLoadError] = useState(false); // letzter Abruf fehlgeschlagen (nie fail-silent)
  const loadingRef = useRef(false);
  const pendingRef = useRef(false);              // trailing-Reload: Trigger während laufendem Request merken

  const load = useCallback(async () => {
    // Läuft bereits ein Reload, wird der Trigger NICHT verworfen, sondern gemerkt:
    // der laufende Request kann eine gerade erst geschriebene Entscheidung noch nicht
    // enthalten — nach dessen Abschluss wird deshalb genau einmal nachgeladen.
    if (loadingRef.current) { pendingRef.current = true; return; }
    loadingRef.current = true;
    try {
      do {
        pendingRef.current = false;
        try {
          const [onb, meRes] = await Promise.all([api.get('/partners/onboarding'), api.get('/partners/me')]);
          setSt(onb.data); setMe(meRes.data);
          setLoadError(false);
        } catch {
          setLoadError(true);                    // Fehlerzustand führen — Anzeige entscheidet unten
        }
      } while (pendingRef.current);              // gemerkter Trigger → sofort ein weiterer Durchlauf
    } finally { loadingRef.current = false; }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Rückkehr in den Tab/Fokus → Stand neu laden (Admin kann inzwischen entschieden haben).
  useEffect(() => {
    const onVisibility = () => { if (!document.hidden) load(); };
    const onFocus = () => load();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [load]);

  // Socket: 'document_decided' an partner_<uid> (Vertrag Stufe C) → sofort neu laden + melden.
  useEffect(() => {
    const socket = io(socketOrigin(),
      { auth: { token: localStorage.getItem('partner_token') }, extraHeaders: { 'ngrok-skip-browser-warning': 'true' } });
    // Raum-Beitritt anfordern: der Server tritt partner_<uid> AUSSCHLIESSLICH auf dieses
    // Client-Emit hin bei (server.js: socket.on('join_partner', …)); ohne Emit erreicht
    // 'document_decided' (raumgezielt io.to(`partner_<uid>`)) den Client NIE.
    // 'connect' feuert auch nach jedem Reconnect → der Raum wird immer wieder betreten.
    socket.on('connect', () => socket.emit('join_partner'));
    socket.on('document_decided', (d) => {
      if (d?.decision === 'approved') toast.success('Dokument freigegeben');
      else toast.error(`Dokument abgelehnt${d?.reason ? `: ${d.reason}` : ''}`);
      load();
    });
    return () => socket.disconnect();
  }, [load]);

  // Upload = neue Revision einreichen (Backend ersetzt die eigene offene Revision atomar).
  const upload = async (documentType, file, extra = {}) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [documentType]: true }));
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('document_type', documentType);
      if (extra.issue_date) form.append('issue_date', extra.issue_date);
      if (extra.expires_at) form.append('expires_at', extra.expires_at);
      await api.post('/partners/documents', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Dokument eingereicht – es wird geprüft');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Upload fehlgeschlagen');
      if (e.response?.status === 409) await load();   // Konflikt: Stand ist veraltet → neu ziehen
    } finally {
      setUploading((u) => ({ ...u, [documentType]: false }));
    }
  };

  // Vorschau der EIGENEN Dokumente: Owner-Route mit Header-Auth als Blob, Objekt-URL
  // öffnen und wieder freigeben. Bewusst KEIN ?token=-Link (Vertrag Stufe B: 401).
  const openPreview = async (docId) => {
    try {
      const r = await api.get(`/partners/documents/${docId}/content`, { responseType: 'blob' });
      const blob = r.data instanceof Blob
        ? r.data
        : new Blob([r.data], { type: r.headers?.['content-type'] || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Vorschau nicht verfügbar');
    }
  };

  const saveBank = async () => {
    try {
      await api.patch('/partners/me/bank', bank);
      toast.success('Bankverbindung hinterlegt');
      await load();
    } catch (e) { toast.error(e.response?.data?.error || 'Ungültige IBAN'); }
  };

  if (!st || !me) {
    // Schon der ERSTE Abruf fehlgeschlagen → klarer Fehlerzustand mit Retry-Knopf
    // statt ewigem „Wird geladen…" (Heilung nicht dem Zufalls-Fokuswechsel überlassen).
    if (loadError) {
      return (
        <div data-testid="load-error" style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '16px 18px', maxWidth: 480 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 12 }}>
            <AlertTriangle size={16} color="#dc2626" style={{ flexShrink: 0, marginTop: 1 }} />
            <span style={{ fontSize: 13, color: '#991b1b', fontWeight: 600, lineHeight: 1.5 }}>
              Dokumente konnten nicht geladen werden. Bitte prüfe deine Verbindung.
            </span>
          </div>
          <button data-testid="load-retry" onClick={load} style={primaryBtn}>Erneut versuchen</button>
        </div>
      );
    }
    return <div style={{ color: '#6b7280', fontSize: 14 }}>Wird geladen…</div>;
  }

  const views = buildChecklist(st.documents);
  const partner = me.partner || {};
  const allDocs = [...(me.documents || [])].sort((a, b) =>
    String(b.created_at || '').localeCompare(String(a.created_at || '')) || (b.revision || 1) - (a.revision || 1));

  // Anzeigename je document_type: aus den Backend-Punkten + Upload-Wegen abgeleitet.
  const typeLabels = {};
  for (const v of views) for (const o of uploadOptionsFor(v)) typeLabels[o.type] = o.label === v.label ? v.label : `${v.label} (${o.label})`;
  for (const o of OPTIONAL_UPLOADS) typeLabels[o.type] = typeLabels[o.type] || o.label;
  // ZEIT-HELFER-001: reine Datumswerte (expires_at) UND SQLite-UTC (created_at) über lib/zeit.
  const fmtDate = (d) => (d ? nurDatum(d) : '–');

  const datesFor = (key) => dates[key] || { issue_date: '', expires_at: '' };
  const setDate = (key, field, value) => setDates((s) => ({ ...s, [key]: { ...datesFor(key), [field]: value } }));

  return (
    <div>
      <PageHeader
        title="Dokumente"
        description="Pflicht-Unterlagen, Prüfstatus und Erneuerungen — Grundlage ist die Admin-Prüfung"
      />

      {/* Reload fehlgeschlagen, aber Daten vorhanden → dezenter Hinweis statt fail-silent:
          der angezeigte Stand kann veraltet sein (z. B. Entscheidung schon gefallen). */}
      {loadError && (
        <div data-testid="stale-hint" style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 14px', marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <AlertTriangle size={14} color="#b45309" style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: '#92400e', fontWeight: 600 }}>
            Aktualisierung fehlgeschlagen — der angezeigte Stand kann veraltet sein.
          </span>
          <button data-testid="stale-retry" onClick={load}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#92400e', fontSize: 12, fontWeight: 700, textDecoration: 'underline', padding: 0 }}>
            Erneut versuchen
          </button>
        </div>
      )}

      {/* Gesperrtes Konto: der Erneuerungspfad bleibt AUSDRÜCKLICH offen. */}
      {partner.suspended_reason && (
        <div data-testid="suspended-hint" style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <XCircle size={16} color="#dc2626" style={{ flexShrink: 0, marginTop: 1 }} />
          <span style={{ fontSize: 13, color: '#991b1b', fontWeight: 600, lineHeight: 1.5 }}>
            {String(partner.suspended_reason).startsWith('admin_gesperrt:')
              ? 'Konto von VOOGO gesperrt. Du kannst hier weiterhin Dokumente hochladen und erneuern — wir heben die Sperre nach der Prüfung auf.'
              : 'Konto gesperrt (Dokument abgelaufen). Du kannst hier weiterhin Dokumente erneuern — nach der Freigabe wird das Konto automatisch entsperrt.'}
          </span>
        </div>
      )}

      {/* Katalog-Punkte — 1:1 aus dem Backend, in Backend-Reihenfolge */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
        {views.map((v) => {
          const opts = uploadOptionsFor(v);
          const anyUploading = opts.some((o) => uploading[o.type]);
          const effState = anyUploading ? 'uploading' : v.state;
          const p = statusPresentation(effState);
          const kDates = datesFor(v.key);
          const needsDates = needsIssuedAndExpiry(v.key);
          const datesOk = !needsDates || (kDates.issue_date && kDates.expires_at);
          const rows = revisionRows(v);
          return (
            <div key={v.key} data-testid={`doc-item-${v.key}`}
              style={{ background: '#fff', borderRadius: 12, padding: 16, border: `1px solid ${v.state === 'approved' ? '#F3E5F5' : (v.state === 'rejected' || v.state === 'expired' || v.state === 'complianceLocked') ? '#fecaca' : '#e5e7eb'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 800 }}>{v.label}</div>
                  {v.legacyHint && <div style={{ fontSize: 12, color: '#92400e', marginTop: 2 }}>{v.legacyHint}</div>}
                </div>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, color: p.color, background: p.bg, padding: '3px 10px', borderRadius: 8 }}>
                  {stateIcon[effState] || stateIcon.missing} {p.label}
                </span>
              </div>

              {/* Ablehnungsgrund bleibt persistent sichtbar */}
              {v.rejectionReason && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#dc2626', fontWeight: 600 }}>
                  Grund: {v.rejectionReason}
                </div>
              )}
              {v.state === 'expired' && v.expiresAt && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#dc2626' }}>Abgelaufen am {fmtDate(v.expiresAt)} — bitte erneuern.</div>
              )}
              {v.state === 'complianceLocked' && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#991b1b' }}>Dieser Punkt ist administrativ gesperrt. Bitte wende dich an den Support.</div>
              )}

              {/* effective vs. latest — GETRENNTE Zeilen (pending heißt NUR eingereicht) */}
              {rows.length > 0 && (
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {rows.map((r) => (
                    <div key={r.kind} data-testid={`rev-${r.kind}-${v.key}`}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: r.kind === 'effective' ? '#374151' : '#4b5563', background: '#f9fafb', borderRadius: 8, padding: '6px 10px' }}>
                      <span style={{ fontWeight: 700 }}>{r.kind === 'effective' ? 'Wirksam' : 'Neueste Einreichung'}</span>
                      <span>Rev. {r.revision}</span>
                      <span>· {r.kind === 'effective' ? 'Freigegeben' : r.statusLabel}</span>
                      {r.expiresAt && <span>· gültig bis {fmtDate(r.expiresAt)}</span>}
                      <button data-testid={`rev-preview-${r.documentId}`} onClick={() => openPreview(r.documentId)} title="Ansehen"
                        style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4, background: 'transparent', border: 'none', cursor: 'pointer', color: '#374151', fontSize: 12, fontWeight: 600, padding: 2 }}>
                        <Eye size={13} /> Ansehen
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Erneuerung läuft: Hinweis statt Doppel-Formular — Ersetzen bleibt möglich */}
              {v.renewalOpen && v.state !== 'renewalPending' && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#4b5563' }}>Erneuerung eingereicht — wird geprüft.</div>
              )}

              {/* Upload-Bereich */}
              {v.isBank ? (
                <div style={{ marginTop: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', marginBottom: 8 }}>
                    <Landmark size={14} color="#4b5563" />
                    {partner.iban
                      ? <span>IBAN hinterlegt: <strong>···{String(partner.iban).slice(-4)}</strong> — IBAN und Nachweis werden GEMEINSAM geprüft.</span>
                      : <span>Noch keine IBAN hinterlegt — IBAN und Nachweis werden gemeinsam geprüft.</span>}
                  </div>
                  {!partner.iban && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                      <input placeholder="IBAN" value={bank.iban} onChange={(e) => setBank((s) => ({ ...s, iban: e.target.value }))} style={{ ...inp, gridColumn: '1 / -1' }} />
                      <input placeholder="BIC (optional)" value={bank.bic} onChange={(e) => setBank((s) => ({ ...s, bic: e.target.value }))} style={inp} />
                      <input placeholder="Kontoinhaber" value={bank.bank_holder} onChange={(e) => setBank((s) => ({ ...s, bank_holder: e.target.value }))} style={inp} />
                      <button onClick={saveBank} style={{ ...primaryBtn, gridColumn: '1 / -1' }}>Bankverbindung speichern</button>
                    </div>
                  )}
                  <UploadButton option={{ type: 'bank_nachweis', label: `Bank-/Inhabernachweis ${uploadActionLabel(v.state).toLowerCase()}` }}
                    uploading={uploading} onFile={(t, f) => upload(t, f)} />
                </div>
              ) : (
                <div style={{ marginTop: 12 }}>
                  {needsDates && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                      <label style={dateLbl}>Ausstellungsdatum
                        <input data-testid={`issued-${v.key}`} type="date" value={kDates.issue_date}
                          onChange={(e) => setDate(v.key, 'issue_date', e.target.value)} style={inp} />
                      </label>
                      <label style={dateLbl}>Ablaufdatum
                        <input data-testid={`expires-${v.key}`} type="date" value={kDates.expires_at}
                          onChange={(e) => setDate(v.key, 'expires_at', e.target.value)} style={inp} />
                      </label>
                    </div>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {opts.map((o) => (
                      <UploadButton key={o.type}
                        option={{ ...o, label: opts.length > 1 ? o.label : `${o.label !== v.label ? o.label : ''} ${uploadActionLabel(v.state)}`.trim() }}
                        disabled={!datesOk} uploading={uploading}
                        onFile={(t, f) => upload(t, f, needsDates ? kDates : {})} />
                    ))}
                  </div>
                  {needsDates && !datesOk && (
                    <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>Für die Konzession bitte Ausstellungs- UND Ablaufdatum angeben, dann hochladen.</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Optionale weitere Unterlagen (Spiegel der optionalen Backend-Katalog-Punkte, KEINE Pflicht) */}
      <div style={{ background: '#fff', borderRadius: 12, padding: 16, border: '1px solid #e5e7eb', marginBottom: 20 }}>
        <h3 style={{ fontSize: 13, fontWeight: 800, marginBottom: 10 }}>Weitere Unterlagen (optional)</h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={optType} onChange={(e) => setOptType(e.target.value)}
            style={{ padding: '9px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, background: '#fff' }}>
            {OPTIONAL_UPLOADS.map((o) => <option key={o.type} value={o.type}>{o.label}</option>)}
          </select>
          <UploadButton option={{ type: optType, label: 'Hochladen' }} uploading={uploading} onFile={(t, f) => upload(t, f)} />
        </div>
      </div>

      {/* Alle Revisionen (inkl. ersetzter) — Entscheidung bleibt nachvollziehbar */}
      {allDocs.length > 0 && (
        <div data-testid="all-documents" style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid #f3f4f6' }}>
            <h3 style={{ fontSize: 13, fontWeight: 800 }}>Alle eingereichten Dokumente</h3>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: '#f9fafb' }}>
                {['Typ', 'Rev.', 'Status', 'Gültig bis', 'Hochgeladen', ''].map((h, i) => (
                  <th key={i} style={{ padding: '10px 14px', fontSize: 12, color: '#6b7280', fontWeight: 700, textAlign: 'left', textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {allDocs.map((doc, i) => {
                  const ls = doc.lifecycle_status || (doc.status === 'pending' ? 'pending_review' : doc.status);
                  const pres = { approved: '#9C27B0', rejected: '#dc2626', expired: '#dc2626' }[ls] || '#4b5563';
                  return (
                    <tr key={doc.id} style={{ borderTop: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '11px 14px', fontSize: 13, fontWeight: 600 }}>{typeLabels[doc.document_type] || doc.document_type}</td>
                      <td style={{ padding: '11px 14px', fontSize: 12, color: '#4b5563' }}>{doc.revision || 1}</td>
                      <td style={{ padding: '11px 14px' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: pres }}>{lifecycleLabel(ls)}</span>
                        {ls === 'rejected' && doc.rejection_reason && <span style={{ fontSize: 12, color: '#dc2626' }}> · {doc.rejection_reason}</span>}
                      </td>
                      <td style={{ padding: '11px 14px', fontSize: 12, color: '#6b7280' }}>{fmtDate(doc.expires_at)}</td>
                      <td style={{ padding: '11px 14px', fontSize: 12, color: '#6b7280' }}>{fmtDate(doc.created_at)}</td>
                      <td style={{ padding: '11px 14px' }}>
                        <button data-testid={`preview-${doc.id}`} onClick={() => openPreview(doc.id)}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#f4f5f6', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#374151' }}>
                          <Eye size={13} /> Ansehen
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// Datei-Auswahl-Knopf (versteckter file-Input). data-testid je document_type.
function UploadButton({ option, uploading, onFile, disabled }) {
  const busy = !!uploading[option.type];
  const off = disabled || busy;
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 14px', background: '#f4f5f6', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#374151', cursor: off ? 'not-allowed' : 'pointer', opacity: off ? 0.55 : 1 }}>
      <Upload size={14} /> {busy ? 'Wird hochgeladen…' : option.label}
      <input data-testid={`upload-input-${option.type}`} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp"
        disabled={off} style={{ display: 'none' }}
        onChange={(e) => { onFile(option.type, e.target.files[0]); e.target.value = ''; }} />
    </label>
  );
}

const inp = { padding: '9px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box' };
const dateLbl = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 700, color: '#4b5563' };
const primaryBtn = { padding: '10px 16px', background: '#000', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer' };
